import React, { useState } from 'react';
import { useLang } from '../i18n';
import PublicNav from '../components/PublicNav';

const c = {
    root: { minHeight: '100vh', background: '#050810', color: '#f1f5f9', fontFamily: '"Segoe UI", system-ui, sans-serif' },
    wrap: { display: 'flex', maxWidth: 1200, margin: '0 auto', padding: '48px' },
    sidebar:{ width: 220, flexShrink: 0, marginRight: 48 },
    main:   { flex: 1, minWidth: 0 },
    sideLink: (active) => ({ display: 'block', padding: '8px 12px', borderRadius: 8, fontSize: 13, color: active ? '#60a5fa' : '#64748b', background: active ? 'rgba(29,78,216,0.1)' : 'none', textDecoration: 'none', marginBottom: 2, cursor: 'pointer', border: active ? '1px solid rgba(29,78,216,0.2)' : '1px solid transparent' }),
    h1:     { fontSize: 40, fontWeight: 900, marginBottom: 8, letterSpacing: '-0.5px' },
    sub:    { fontSize: 16, color: '#64748b', marginBottom: 48, lineHeight: 1.7 },
    section:{ marginBottom: 56 },
    h2:     { fontSize: 22, fontWeight: 800, marginBottom: 12, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' },
    h3:     { fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#60a5fa' },
    p:      { fontSize: 14, color: '#94a3b8', lineHeight: 1.8, marginBottom: 12 },
    code:   { fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 4, fontSize: 13, color: '#06b6d4' },
    codeBlock: { background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '18px 20px', fontSize: 13, fontFamily: 'monospace', lineHeight: 1.7, overflowX: 'auto', marginBottom: 16, whiteSpace: 'pre' },
    table:  { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 },
    th:     { padding: '10px 12px', background: 'rgba(255,255,255,0.04)', color: '#60a5fa', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    td:     { padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#94a3b8', verticalAlign: 'top' },
    info:   { background: 'rgba(29,78,216,0.08)', border: '1px solid rgba(29,78,216,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#94a3b8', lineHeight: 1.8, marginBottom: 12 },
    warn:   { background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#94a3b8', lineHeight: 1.8, marginBottom: 12 },
    footer: { borderTop: '1px solid rgba(255,255,255,0.06)', padding: '24px 48px', color: '#334155', fontSize: 12 },
};

const SECTIONS = [
    { id: 'intro',        label: 'Introduction' },
    { id: 'auth',         label: 'Authentication' },
    { id: 'endpoint',     label: 'Common Parameters' },
    { id: 'services',     label: 'action=services' },
    { id: 'balance',      label: 'action=balance' },
    { id: 'add',          label: 'action=add' },
    { id: 'status',       label: 'action=status' },
    { id: 'cancel',       label: 'action=cancel' },
    { id: 'link-types',   label: 'Link Types' },
    { id: 'subscriptions',label: 'Subscriptions' },
    { id: 'refund',       label: 'Refund Policy' },
    { id: 'statuses',     label: 'Order Statuses' },
    { id: 'errors',       label: 'Error Codes' },
    { id: 'discount',     label: 'Volume Discounts' },
    { id: 'panel-setup',  label: 'Panel Integration' },
];

// 2026-06-12 — avviso auto-sync multilingua (rivenditori PerfectPanel: senza auto-sync i nostri
// cambi prezzo non arrivano → vendono a prezzi vecchi → perdono soldi, vedi caso Jack).
const AUTO_SYNC_WARN = {
    en: "⚠️ Be sure to TURN ON AUTO-SYNC for OrbitGrowth in your panel's provider settings — otherwise our price updates won't reach your panel and you may sell at outdated rates.",
    it: "⚠️ Assicurati di ATTIVARE l'AUTO-SYNC per OrbitGrowth nelle impostazioni provider del tuo pannello — altrimenti i nostri aggiornamenti prezzi non arrivano e potresti vendere a prezzi vecchi.",
    es: "⚠️ Asegúrate de ACTIVAR la AUTO-SINCRONIZACIÓN de OrbitGrowth en los ajustes de proveedor de tu panel — de lo contrario nuestras actualizaciones de precios no llegarán y podrías vender a precios desactualizados.",
    de: "⚠️ Aktiviere unbedingt die AUTO-SYNCHRONISIERUNG für OrbitGrowth in den Anbieter-Einstellungen deines Panels — sonst erreichen dich unsere Preisänderungen nicht und du verkaufst zu veralteten Preisen.",
    fr: "⚠️ Pensez à ACTIVER l'AUTO-SYNC pour OrbitGrowth dans les paramètres fournisseur de votre panel — sinon nos mises à jour de prix ne vous parviennent pas et vous risquez de vendre à des tarifs obsolètes.",
    ru: "⚠️ Обязательно ВКЛЮЧИТЕ АВТОСИНХРОНИЗАЦИЮ для OrbitGrowth в настройках провайдера вашей панели — иначе обновления цен не дойдут, и вы можете продавать по устаревшим ценам.",
    pt: "⚠️ Não esqueça de ATIVAR a AUTO-SINCRONIZAÇÃO do OrbitGrowth nas configurações de provedor do seu painel — caso contrário nossas atualizações de preços não chegam e você pode vender com preços desatualizados.",
    tr: "⚠️ Panelinizin sağlayıcı ayarlarında OrbitGrowth için OTOMATİK SENKRONİZASYONU AÇTIĞINIZDAN emin olun — aksi halde fiyat güncellemelerimiz ulaşmaz ve eski fiyatlarla satış yapabilirsiniz.",
    ja: "⚠️ パネルのプロバイダー設定で OrbitGrowth の自動同期を必ずオンにしてください。オンにしないと価格更新が反映されず、古い価格で販売してしまう可能性があります。",
    ar: "⚠️ تأكد من تفعيل المزامنة التلقائية لـ OrbitGrowth في إعدادات المزود في لوحتك — وإلا فلن تصلك تحديثات الأسعار وقد تبيع بأسعار قديمة.",
    zh: "⚠️ 请务必在面板的供应商设置中为 OrbitGrowth 开启自动同步，否则我们的价格更新将无法同步，您可能以过时价格销售。",
};

export default function ApiDocs() {
    const { t, lang } = useLang();
    const [active, setActive] = useState('intro');
    const api = t('api') || {};

    const scrollTo = (id) => {
        setActive(id);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const BASE_URL = window.location.origin;

    return (
        <div style={c.root}>
            <PublicNav />

            <div style={c.wrap}>
                {/* Sidebar */}
                <aside style={c.sidebar}>
                    <div style={{ position: 'sticky', top: 24 }}>
                        <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>API Documentation</div>
                        {SECTIONS.map(s => (
                            <div key={s.id} style={c.sideLink(active === s.id)} onClick={() => scrollTo(s.id)}>
                                {s.label}
                            </div>
                        ))}
                    </div>
                </aside>

                {/* Content */}
                <div style={c.main}>
                    <h1 style={c.h1}>{api.title || 'API Reference'}</h1>
                    <p style={c.sub}>
                        OrbitGrowth uses a <strong style={{ color: '#60a5fa' }}>PerfectPanel v2</strong>-compatible API.
                        If you run your own reseller panel (PerfectPanel, SMMKings, or any compatible panel), add OrbitGrowth as a provider directly in your panel's <em>API Providers / Suppliers</em> section — enter our base URL and your OrbitGrowth API key. Your existing panel setup stays untouched; you simply gain access to our services as a new supplier.
                    </p>
                    <div style={{ background: '#422006', border: '1px solid #f59e0b', borderRadius: 8, padding: '14px 18px', margin: '8px 0 20px', color: '#fcd34d', fontWeight: 600, lineHeight: 1.5 }}>
                        {AUTO_SYNC_WARN[lang] || AUTO_SYNC_WARN.en}
                    </div>

                    {/* INTRO */}
                    <div id="intro" style={c.section}>
                        <h2 style={c.h2}>Introduction</h2>
                        <p style={c.p}>
                            The OrbitGrowth API lets you automate the purchase of Instagram services: likes, followers, comments, views, and subscriptions.
                            All requests are sent via <span style={c.code}>POST /api/v2</span> with body encoded as{' '}
                            <span style={c.code}>application/x-www-form-urlencoded</span> or <span style={c.code}>application/json</span>.
                        </p>
                        <p style={c.p}><strong>Base URL:</strong></p>
                        <div style={c.codeBlock}>{BASE_URL}/api/v2</div>
                        <p style={c.p}>Your API key is available in the <em>API Key</em> section of your user dashboard. Each account has a unique key.</p>
                    </div>

                    {/* AUTH */}
                    <div id="auth" style={c.section}>
                        <h2 style={c.h2}>Authentication</h2>
                        <p style={c.p}>Include your API Key in every request. You have three options:</p>
                        <h3 style={c.h3}>Option 1 — Body parameter (recommended)</h3>
                        <div style={c.codeBlock}>{`POST /api/v2
Content-Type: application/json

{
  "key":    "your-api-key",
  "action": "balance"
}`}</div>
                        <h3 style={c.h3}>Option 2 — HTTP header</h3>
                        <div style={c.codeBlock}>{`POST /api/v2
X-Api-Key: your-api-key
Content-Type: application/json

{ "action": "balance" }`}</div>
                        <h3 style={c.h3}>Option 3 — Query parameter</h3>
                        <div style={c.codeBlock}>{`POST /api/v2?key=your-api-key
Content-Type: application/json

{ "action": "balance" }`}</div>
                        <p style={c.p}>If the key is missing or invalid, you will receive <span style={c.code}>HTTP 401</span>.</p>
                    </div>

                    {/* ENDPOINT */}
                    <div id="endpoint" style={c.section}>
                        <h2 style={c.h2}>Common Parameters</h2>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Parameter</th><th style={c.th}>Type</th><th style={c.th}>Description</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}><span style={c.code}>key</span></td><td style={c.td}>string</td><td style={c.td}>User API Key (required unless using X-Api-Key header)</td></tr>
                                <tr><td style={c.td}><span style={c.code}>action</span></td><td style={c.td}>string</td><td style={c.td}>Action to perform: <span style={c.code}>services</span>, <span style={c.code}>balance</span>, <span style={c.code}>add</span>, <span style={c.code}>status</span>, <span style={c.code}>cancel</span></td></tr>
                            </tbody>
                        </table></div>
                    </div>

                    {/* SERVICES */}
                    <div id="services" style={c.section}>
                        <h2 style={c.h2}>action=services</h2>
                        <p style={c.p}>Returns the list of all active services with prices and quantity limits.</p>
                        <h3 style={c.h3}>Request</h3>
                        <div style={c.codeBlock}>{`POST /api/v2
{ "key": "YOUR_KEY", "action": "services" }`}</div>
                        <h3 style={c.h3}>Response</h3>
                        <div style={c.codeBlock}>{`[
  {
    "service":     1017,
    "name":        "IG Likes - Male+Female",
    "type":        "Default",
    "category":    "Likes",
    "rate":        "3.5000",       // price per 1000 units in EUR
    "min":         "25",
    "max":         "5000",
    "dripfeed":    false,
    "refill":      false,
    "cancel":      true,
    "description": "Link type: post_url"   // see Link Types section
  },
  ...
]`}</div>
                        <p style={c.p}>
                            The <span style={c.code}>description</span> field contains the required <strong>link type</strong> for the service.
                            See the <em>Link Types</em> section below for details on what URL format each service expects.
                        </p>
                    </div>

                    {/* BALANCE */}
                    <div id="balance" style={c.section}>
                        <h2 style={c.h2}>action=balance</h2>
                        <p style={c.p}>Returns the current account balance in EUR.</p>
                        <div style={c.codeBlock}>{`// Request
{ "key": "YOUR_KEY", "action": "balance" }

// Response
{
  "balance":  "42.5000",
  "currency": "EUR"
}`}</div>
                    </div>

                    {/* ADD */}
                    <div id="add" style={c.section}>
                        <h2 style={c.h2}>action=add</h2>
                        <p style={c.p}>Creates a new order. Balance is deducted immediately.</p>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Parameter</th><th style={c.th}>Required</th><th style={c.th}>Description</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}><span style={c.code}>service</span></td><td style={c.td}>Yes</td><td style={c.td}>Service ID (from <span style={c.code}>action=services</span>)</td></tr>
                                <tr><td style={c.td}><span style={c.code}>link</span></td><td style={c.td}>Yes</td><td style={c.td}>Target URL or username (see <em>Link Types</em> below)</td></tr>
                                <tr><td style={c.td}><span style={c.code}>quantity</span></td><td style={c.td}>Yes</td><td style={c.td}>Quantity (must be within min/max of the service)</td></tr>
                            </tbody>
                        </table></div>
                        <h3 style={c.h3}>SMM order example</h3>
                        <div style={c.codeBlock}>{`// Request
{
  "key":      "YOUR_KEY",
  "action":   "add",
  "service":  1017,
  "link":     "https://www.instagram.com/p/XXXXX/",
  "quantity": 100
}

// Response (success)
{ "order": 12345 }

// Response (insufficient balance)
{ "error": "Insufficient balance. Required: \u20ac0.3500, Available: \u20ac0.1200" }

// Response (wrong link type)
{ "error": "This service requires a post URL (e.g. instagram.com/p/...)" }`}</div>
                        <h3 style={c.h3}>Subscription order example</h3>
                        <div style={c.codeBlock}>{`// Request
{
  "key":      "YOUR_KEY",
  "action":   "add",
  "service":  2010,
  "link":     "myusername",
  "quantity": 50
}

// Response (success)
{ "order": 12346 }`}</div>
                        <p style={c.info}>
                            <strong style={{ color: '#60a5fa' }}>Subscription services</strong> use <span style={c.code}>link</span> = your Instagram username (no URL needed).
                            The <span style={c.code}>quantity</span> is the number of real accounts that will follow and interact with your profile daily for the subscription duration (30, 90, or 180 days).
                            See the <em>Subscriptions</em> section for details.
                        </p>
                    </div>

                    {/* STATUS */}
                    <div id="status" style={c.section}>
                        <h2 style={c.h2}>action=status</h2>
                        <p style={c.p}>Check the status of one or more orders. Supports both single and batch requests.</p>
                        <h3 style={c.h3}>Single order</h3>
                        <div style={c.codeBlock}>{`// Request
{ "key": "YOUR_KEY", "action": "status", "order": 12345 }

// Response
{
  "charge":      "0.3500",
  "start_count": "1042",
  "status":      "In progress",
  "remains":     "87",
  "currency":    "EUR"
}`}</div>
                        <h3 style={c.h3}>Multiple orders (batch)</h3>
                        <div style={c.codeBlock}>{`// Request
{ "key": "YOUR_KEY", "action": "status", "orders": "12345,12346,12347" }

// Response
{
  "12345": { "charge": "0.35", "start_count": "1042", "status": "Completed", "remains": "0", "currency": "EUR" },
  "12346": { "charge": "0.70", "start_count": "500",  "status": "In progress", "remains": "50", "currency": "EUR" },
  "12347": { "error": "Order not found" }
}`}</div>
                        <p style={c.p}>
                            See the <em>Order Statuses</em> section for a full list of possible <span style={c.code}>status</span> values.
                        </p>
                    </div>

                    {/* CANCEL */}
                    <div id="cancel" style={c.section}>
                        <h2 style={c.h2}>action=cancel</h2>
                        <p style={c.p}>Cancel one or more orders. See the <em>Refund Policy</em> section for refund details.</p>
                        <h3 style={c.h3}>Single order</h3>
                        <div style={c.codeBlock}>{`// Request
{ "key": "YOUR_KEY", "action": "cancel", "order": 12345 }

// Response (success)
{ "12345": 1 }

// Response (cannot cancel)
{ "12345": "Cannot cancel: status is completed" }`}</div>
                        <h3 style={c.h3}>Multiple orders (batch)</h3>
                        <div style={c.codeBlock}>{`// Request
{ "key": "YOUR_KEY", "action": "cancel", "orders": "12345,12346" }

// Response
{
  "12345": 1,
  "12346": "Cannot cancel: status is completed"
}`}</div>
                        <p style={c.p}>
                            The response is a JSON object where each key is the order ID.
                            Value <span style={c.code}>1</span> = successfully cancelled.
                            A string value indicates the error reason.
                        </p>
                        <p style={c.p}>Only orders with status <span style={c.code}>Pending</span> or <span style={c.code}>In progress</span> can be cancelled.</p>
                    </div>

                    {/* LINK TYPES */}
                    <div id="link-types" style={c.section}>
                        <h2 style={c.h2}>Link Types</h2>
                        <p style={c.p}>
                            Each service requires a specific type of link. The required link type is shown
                            in the <span style={c.code}>description</span> field of the <span style={c.code}>action=services</span> response.
                        </p>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Link Type</th><th style={c.th}>Format</th><th style={c.th}>Used by</th></tr></thead>
                            <tbody>
                                <tr>
                                    <td style={c.td}><span style={c.code}>username</span></td>
                                    <td style={c.td}><span style={c.code}>myprofile</span> or <span style={c.code}>https://instagram.com/myprofile</span></td>
                                    <td style={c.td}>Followers, Profile Visits, Story Views (all), Subscriptions</td>
                                </tr>
                                <tr>
                                    <td style={c.td}><span style={c.code}>post_url</span></td>
                                    <td style={c.td}><span style={c.code}>https://instagram.com/p/ABC123/</span></td>
                                    <td style={c.td}>Post Likes, Comments, Saves, Reposts</td>
                                </tr>
                                <tr>
                                    <td style={c.td}><span style={c.code}>reel_url</span></td>
                                    <td style={c.td}><span style={c.code}>https://instagram.com/reel/ABC123/</span></td>
                                    <td style={c.td}>Reel Views, Reel Likes, Reel Comments</td>
                                </tr>
                                <tr>
                                    <td style={c.td}><span style={c.code}>story_url</span></td>
                                    <td style={c.td}><span style={c.code}>https://instagram.com/stories/user/123456/</span></td>
                                    <td style={c.td}>Story Likes, Story Reactions, Story Replies</td>
                                </tr>
                            </tbody>
                        </table></div>
                        <p style={c.warn}>
                            <strong style={{ color: '#eab308' }}>Important:</strong> If you provide a username where a post/reel/story URL is required (or vice versa),
                            the order will be marked as <span style={c.code}>Partial</span> and refunded after 5 failed delivery attempts.
                        </p>
                    </div>

                    {/* SUBSCRIPTIONS */}
                    <div id="subscriptions" style={c.section}>
                        <h2 style={c.h2}>Subscriptions</h2>
                        <p style={c.p}>
                            Subscription services provide long-term, daily engagement with your Instagram profile.
                            Unlike one-time SMM orders, subscriptions deliver continuous interactions over 30, 90, or 180 days.
                        </p>
                        <h3 style={c.h3}>How it works</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Step</th><th style={c.th}>What happens</th><th style={c.th}>When</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}>1</td><td style={c.td}>You place a subscription order with <span style={c.code}>action=add</span></td><td style={c.td}>Immediately</td></tr>
                                <tr><td style={c.td}>2</td><td style={c.td}>24-hour grace period (you can cancel for a full refund)</td><td style={c.td}>0 - 24 hours</td></tr>
                                <tr><td style={c.td}>3</td><td style={c.td}>Real accounts start following your profile</td><td style={c.td}>After 24 hours</td></tr>
                                <tr><td style={c.td}>4</td><td style={c.td}>Daily interactions: likes, story views, comments on your posts</td><td style={c.td}>Every day</td></tr>
                                <tr><td style={c.td}>5</td><td style={c.td}>Subscription expires, accounts unfollow gracefully</td><td style={c.td}>After 30/90/180 days</td></tr>
                            </tbody>
                        </table></div>
                        <h3 style={c.h3}>Daily delivery</h3>
                        <p style={c.p}>
                            Each subscribed account performs a mix of interactions daily: profile visits, post likes, story views,
                            and occasional AI-generated comments. The interactions are spread naturally throughout the day
                            to look organic. You can track daily delivery in the <em>Subscriptions</em> section of your dashboard.
                        </p>
                        <h3 style={c.h3}>Account replacement</h3>
                        <p style={c.p}>
                            If an account becomes unavailable during your subscription, it is automatically replaced
                            with a new one that follows your profile and continues the daily interactions.
                            No action is needed from your side.
                        </p>
                        <p style={c.warn}>
                            <strong style={{ color: '#eab308' }}>Cancellation:</strong> Subscriptions can only be cancelled within the first 24 hours (grace period) for a full refund.
                            After the grace period, subscriptions are non-refundable.
                        </p>
                    </div>

                    {/* REFUND POLICY */}
                    <div id="refund" style={c.section}>
                        <h2 style={c.h2}>Refund Policy</h2>
                        <p style={c.p}>Refunds are calculated automatically when you cancel an order via <span style={c.code}>action=cancel</span>.</p>
                        <h3 style={c.h3}>SMM orders (one-time)</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Delivery progress</th><th style={c.th}>Refund</th><th style={c.th}>Order status after cancel</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}>0% (not started)</td><td style={{ ...c.td, color: '#22c55e', fontWeight: 700 }}>100%</td><td style={c.td}>Canceled</td></tr>
                                <tr><td style={c.td}>1% - 49%</td><td style={{ ...c.td, color: '#eab308', fontWeight: 700 }}>50%</td><td style={c.td}>Partial</td></tr>
                                <tr><td style={c.td}>50% - 100%</td><td style={{ ...c.td, color: '#ef4444', fontWeight: 700 }}>0%</td><td style={c.td}>Partial</td></tr>
                            </tbody>
                        </table></div>
                        <h3 style={c.h3}>Subscription orders</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Timing</th><th style={c.th}>Refund</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}>Within 24-hour grace period</td><td style={{ ...c.td, color: '#22c55e', fontWeight: 700 }}>100%</td></tr>
                                <tr><td style={c.td}>After grace period</td><td style={{ ...c.td, color: '#ef4444', fontWeight: 700 }}>Not cancellable</td></tr>
                            </tbody>
                        </table></div>
                        <p style={c.p}>
                            If the content you linked becomes unavailable (deleted post, private account, etc.),
                            the order is automatically marked <span style={c.code}>Partial</span> after 5 consecutive failed delivery attempts,
                            and the undelivered portion is refunded at 50%.
                        </p>
                    </div>

                    {/* ORDER STATUSES */}
                    <div id="statuses" style={c.section}>
                        <h2 style={c.h2}>Order Statuses</h2>
                        <p style={c.p}>These are the possible values of the <span style={c.code}>status</span> field returned by <span style={c.code}>action=status</span>.</p>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Status</th><th style={c.th}>Description</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}><span style={c.code}>Pending</span></td><td style={c.td}>Order received, waiting to start processing</td></tr>
                                <tr><td style={c.td}><span style={c.code}>In progress</span></td><td style={c.td}>Delivery is in progress. Check <span style={c.code}>remains</span> to see how much is left.</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Completed</span></td><td style={c.td}>All quantity has been delivered successfully</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Partial</span></td><td style={c.td}>Only part of the order was delivered (content unavailable, or cancelled mid-delivery)</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Canceled</span></td><td style={c.td}>Order was cancelled before delivery started</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Error</span></td><td style={c.td}>Order failed due to an internal error</td></tr>
                            </tbody>
                        </table></div>
                        <h3 style={c.h3}>Status flow</h3>
                        <div style={c.codeBlock}>{`Pending --> In progress --> Completed
                 |               |
                 v               v
             Canceled         Partial`}</div>
                    </div>

                    {/* ERRORS */}
                    <div id="errors" style={c.section}>
                        <h2 style={c.h2}>Error Codes</h2>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>HTTP</th><th style={c.th}>Error</th><th style={c.th}>Cause</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}>200</td><td style={c.td}><span style={c.code}>{`{"error": "..."}`}</span></td><td style={c.td}>Logic error (insufficient balance, invalid quantity, order not found, wrong link type)</td></tr>
                                <tr><td style={c.td}>401</td><td style={c.td}>Unauthorized</td><td style={c.td}>API Key missing or invalid</td></tr>
                                <tr><td style={c.td}>403</td><td style={c.td}>Account suspended</td><td style={c.td}>Account disabled by admin</td></tr>
                                <tr><td style={c.td}>500</td><td style={c.td}>Internal error</td><td style={c.td}>Server-side error — please retry or contact support</td></tr>
                            </tbody>
                        </table></div>
                        <h3 style={c.h3}>Common error messages</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Error</th><th style={c.th}>What to do</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}><span style={c.code}>Insufficient balance</span></td><td style={c.td}>Deposit more funds</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Invalid service</span></td><td style={c.td}>Use <span style={c.code}>action=services</span> to get valid service IDs</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Quantity out of range</span></td><td style={c.td}>Check <span style={c.code}>min</span>/<span style={c.code}>max</span> of the service</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Incorrect order ID</span></td><td style={c.td}>The order does not exist or belongs to another account</td></tr>
                                <tr><td style={c.td}><span style={c.code}>Cannot cancel: status is ...</span></td><td style={c.td}>Only <span style={c.code}>Pending</span> and <span style={c.code}>In progress</span> orders can be cancelled</td></tr>
                            </tbody>
                        </table></div>
                    </div>

                    {/* DISCOUNT */}
                    <div id="discount" style={c.section}>
                        <h2 style={c.h2}>Automatic Volume Discounts</h2>
                        <p style={c.p}>
                            OrbitGrowth automatically applies a discount based on your spending volume over the last 14 days.
                            The discount is recalculated every hour and applied in real time to all new orders.
                        </p>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Volume (14 days)</th><th style={c.th}>Discount</th><th style={c.th}>Tier</th></tr></thead>
                            <tbody>
                                {[
                                    ['\u20ac0 \u2013 \u20ac49.99',   '0%',  'Base'],
                                    ['\u20ac50 \u2013 \u20ac149.99', '10%', 'Lite'],
                                    ['\u20ac150 \u2013 \u20ac299.99','20%', 'Silver'],
                                    ['\u20ac300 \u2013 \u20ac599.99','30%', 'Gold'],
                                    ['\u20ac600+',                    '40%', 'Reseller'],
                                ].map(([v, d, tier]) => (
                                    <tr key={tier}>
                                        <td style={c.td}>{v}</td>
                                        <td style={{ ...c.td, color: d === '0%' ? '#64748b' : '#60a5fa', fontWeight: d !== '0%' ? 700 : 400 }}>{d}</td>
                                        <td style={c.td}>{tier}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                        <p style={c.p}>Your current discount is visible in your dashboard. Prices returned by <span style={c.code}>action=services</span> already include your discount.</p>
                    </div>

                    {/* PANEL INTEGRATION GUIDE */}
                    <div id="panel-setup" style={c.section}>
                        <h2 style={c.h2}>Panel Integration Guide</h2>
                        <p style={c.p}>
                            If you run your own reseller panel (PerfectPanel, SMMKings, JEAP, SMMBuzz, or any panel
                            that supports the standard SMM API format), you can add OrbitGrowth as a <strong style={{ color: '#f1f5f9' }}>provider / supplier</strong> in
                            minutes. No coding required.
                        </p>

                        <h3 style={c.h3}>What you need</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <tbody>
                                <tr><td style={{ ...c.td, fontWeight: 700, color: '#f1f5f9', width: 140 }}>API URL</td><td style={c.td}><span style={c.code}>{BASE_URL}/api/v2</span></td></tr>
                                <tr><td style={{ ...c.td, fontWeight: 700, color: '#f1f5f9' }}>API Key</td><td style={c.td}>Your OrbitGrowth API key (Dashboard {'>'} API Key section)</td></tr>
                            </tbody>
                        </table></div>

                        <h3 style={c.h3}>Setup in PerfectPanel</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Step</th><th style={c.th}>Action</th></tr></thead>
                            <tbody>
                                <tr><td style={{ ...c.td, width: 40, textAlign: 'center', fontWeight: 700 }}>1</td><td style={c.td}>Log in to your PerfectPanel admin dashboard</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>2</td><td style={c.td}>Go to <strong style={{ color: '#f1f5f9' }}>Providers</strong> (left sidebar)</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>3</td><td style={c.td}>Click <strong style={{ color: '#f1f5f9' }}>Add New Provider</strong></td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>4</td><td style={c.td}>Fill in:<br/>
                                    &bull; <strong style={{ color: '#f1f5f9' }}>Name:</strong> OrbitGrowth (or any label you prefer)<br/>
                                    &bull; <strong style={{ color: '#f1f5f9' }}>URL:</strong> <span style={c.code}>{BASE_URL}/api/v2</span><br/>
                                    &bull; <strong style={{ color: '#f1f5f9' }}>API Key:</strong> paste your OrbitGrowth API key<br/>
                                    &bull; <strong style={{ color: '#f1f5f9' }}>Currency:</strong> EUR
                                </td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>5</td><td style={c.td}>Click <strong style={{ color: '#f1f5f9' }}>Save</strong> — your balance will appear automatically</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>6</td><td style={c.td}>Go to <strong style={{ color: '#f1f5f9' }}>Services {'>'} Import from Provider</strong></td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>7</td><td style={c.td}>Select <strong style={{ color: '#f1f5f9' }}>OrbitGrowth</strong> as the provider</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>8</td><td style={c.td}>Select the services you want to offer, set your markup, and activate them</td></tr>
                            </tbody>
                        </table></div>

                        <h3 style={c.h3}>Setup in other panels (SMMKings, JEAP, SMMBuzz, etc.)</h3>
                        <p style={c.p}>
                            The process is nearly identical across all panels that support the standard SMM API:
                        </p>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Step</th><th style={c.th}>Action</th></tr></thead>
                            <tbody>
                                <tr><td style={{ ...c.td, width: 40, textAlign: 'center', fontWeight: 700 }}>1</td><td style={c.td}>Find the <strong style={{ color: '#f1f5f9' }}>Providers</strong>, <strong style={{ color: '#f1f5f9' }}>API Providers</strong>, or <strong style={{ color: '#f1f5f9' }}>Suppliers</strong> section in your admin panel</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>2</td><td style={c.td}>Add a new provider with URL <span style={c.code}>{BASE_URL}/api/v2</span> and your API key</td></tr>
                                <tr><td style={{ ...c.td, textAlign: 'center', fontWeight: 700 }}>3</td><td style={c.td}>Import services, set your prices, and activate</td></tr>
                            </tbody>
                        </table></div>

                        <h3 style={c.h3}>How it works</h3>
                        <div style={c.codeBlock}>{`Your Panel (PerfectPanel / SMMKings / ...)
    |
    |  POST ${BASE_URL.replace(window.location.origin, 'https://orbitgrowth.app')}/api/v2
    |  { key: "YOUR_API_KEY", action: "add", service: 1001, link: "...", quantity: 100 }
    v
OrbitGrowth API
    |
    |  Validates order, deducts balance, creates order
    v
Order Manager --> Device Fleet --> Instagram`}</div>
                        <p style={c.p}>
                            When your customer places an order on your panel, your panel sends it to OrbitGrowth via the API.
                            We process it, deliver the service, and your panel automatically syncs the status updates
                            (<span style={c.code}>Pending</span> {'>'} <span style={c.code}>In progress</span> {'>'} <span style={c.code}>Completed</span>).
                        </p>

                        <p style={c.info}>
                            <strong style={{ color: '#60a5fa' }}>Tip:</strong> Test the connection first
                            by calling <span style={c.code}>action=balance</span> with your API key. If you get
                            back your balance and currency, the integration is working correctly.
                        </p>

                        <h3 style={c.h3}>Supported actions</h3>
                        <div className="og-table-wrap"><table style={c.table}>
                            <thead><tr><th style={c.th}>Action</th><th style={c.th}>Supported</th><th style={c.th}>Notes</th></tr></thead>
                            <tbody>
                                <tr><td style={c.td}><span style={c.code}>services</span></td><td style={{ ...c.td, color: '#22c55e' }}>Yes</td><td style={c.td}>Full catalog with prices, min/max, categories</td></tr>
                                <tr><td style={c.td}><span style={c.code}>add</span></td><td style={{ ...c.td, color: '#22c55e' }}>Yes</td><td style={c.td}>Standard format: service + link + quantity</td></tr>
                                <tr><td style={c.td}><span style={c.code}>status</span></td><td style={{ ...c.td, color: '#22c55e' }}>Yes</td><td style={c.td}>Single and batch (up to 100 orders)</td></tr>
                                <tr><td style={c.td}><span style={c.code}>cancel</span></td><td style={{ ...c.td, color: '#22c55e' }}>Yes</td><td style={c.td}>Single and batch — refund per our <a href="#refund" style={{ color: '#60a5fa' }}>Refund Policy</a></td></tr>
                                <tr><td style={c.td}><span style={c.code}>balance</span></td><td style={{ ...c.td, color: '#22c55e' }}>Yes</td><td style={c.td}>Returns balance in EUR</td></tr>
                                <tr><td style={c.td}><span style={c.code}>refill</span></td><td style={{ ...c.td, color: '#94a3b8' }}>No</td><td style={c.td}>Not applicable — our services use real accounts, not bots</td></tr>
                            </tbody>
                        </table></div>
                    </div>
                </div>
            </div>

            <footer style={c.footer}>
                © {new Date().getFullYear()} OrbitGrowth — PerfectPanel v2 compatible API
                <span style={{ margin: '0 12px', opacity: 0.3 }}>·</span>
                <a href="/terms" style={{ color: '#475569', textDecoration: 'none' }}>Terms of Service</a>
            </footer>
        </div>
    );
}
